"use client";

import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo, ReactNode } from 'react';
import languageService from '../templates/Heritage/utils/services/language';
import { useIdPath } from "./IdPathProvider";
import { getCountryCodeForLanguage } from '@/components/LanguageFlag';
export interface Language {
  id: string;
  name: string;
  code: string;
  flag: string;
  enabled: boolean;
  isDefault?: boolean;
}

export interface LanguageContextType {
  // States
  languages: Language[];
  languagesLoading: boolean;
  languagesError?: string | null;
  currentLanguage: Language | null;

  // Actions
  loadLanguages: (targetIdPath?: string) => Promise<void>;
  setCurrentLanguage: (language: Language) => void;
  getLanguageByCode: (code: string) => Language | undefined;
  getDefaultLanguage: () => Language | undefined;
  getEnabledLanguages: () => Language[];

  // Utilities
  formatLanguageList: () => Record<string, Language>;
  isLanguageSupported: (code: string) => boolean;
  getLanguageFlag: (code: string) => string | undefined;
  getLanguageName: (code: string) => string | undefined;

  // Dynamic title utilities
  initializeDynamicTitles: () => Record<string, string>;
  validateDynamicTitles: (titles: Record<string, string>) => boolean;
  getDisplayTitle: (titles: Record<string, string>) => string;
}

// Default fallback languages
const DEFAULT_LANGUAGES: Language[] = [
  {
    id: 'vi',
    name: 'Tiếng Việt',
    code: 'VIE',
    flag: 'vn',
    enabled: true,
    isDefault: true
  },
  {
    id: 'en',
    name: 'English',
    code: 'ENG',
    flag: 'us',
    enabled: true
  }
];

const normalizeLangKey = (val?: any): string => {
  if (val === null || val === undefined) return '';
  const str = String(val).trim().toLowerCase();
  if (str === 'vn' || str === 'vie') return 'vi';
  if (str === 'us' || str === 'gb' || str === 'eng') return 'en';
  if (str === 'km' || str === 'khm') return 'km';
  if (str === 'jp' || str === 'jpn') return 'ja';
  if (str === 'kr' || str === 'kor') return 'ko';
  if (str === 'cn' || str === 'zho' || str === 'chi') return 'zh';
  return str;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<Language | null>(null);
  const { idPath, isLoading: idPathLoading } = useIdPath();
  const activeRequestIdRef = useRef(0);
  const loadedProjectIdRef = useRef<string | null>(null);
  const idPathRef = useRef(idPath);

  useEffect(() => {
    idPathRef.current = idPath;
  }, [idPath]);

  // Load languages from API
  const loadLanguages = useCallback(async (targetIdPath?: string) => {
    const targetProjectId = String(targetIdPath || idPathRef.current || '').trim();
    const reqId = ++activeRequestIdRef.current;
    const isSameProject = Boolean(loadedProjectIdRef.current && loadedProjectIdRef.current === targetProjectId);

    if (!isSameProject) {
      // Clear previous project data immediately so project A does not bleed into project B
      setLanguages([]);
      setCurrentLanguage(null);
      setLanguagesError(null);
    } else {
      setLanguagesError(null);
    }

    setLanguagesLoading(true);
    try {
      if (!targetProjectId) {
        throw new Error('Chưa xác định được ID dự án để tải ngôn ngữ.');
      }

      const res = await languageService.getAllLanguages(targetProjectId);
      if (reqId !== activeRequestIdRef.current) return;

      let processedLanguages: Language[] = [];

      // Service already returns response.data, so res should be the language array directly
      if (Array.isArray(res)) {
        processedLanguages = res.map((l: any) => ({
          id: l.id || l.code?.toLowerCase() || 'unknown',
          name: l.name || l.full_name || 'Unknown Language',
          code: l.code || l.id?.toUpperCase() || 'UN',
          flag: l.flag || l.avatar || getCountryCodeForLanguage(l.id),
          enabled: l.enabled !== false && l.enabled !== 0 && l.enabled !== '0',
          isDefault: l.is_default === 1 || l.is_default === '1' || l.isDefault === true || l.is_default === true,
        }));
      } else if (res && typeof res === 'object') {

        // If response is an object, check for nested data
        let languageArray = null;

        if (Array.isArray(res.data)) {
          languageArray = res.data;
        } else if (res.data && Array.isArray(res.data.data)) {
          languageArray = res.data.data;
        } else if (Array.isArray(res.languages)) {
          languageArray = res.languages;
        }

        if (languageArray) {
          processedLanguages = languageArray.map((l: any) => ({
            id: l.id || l.code?.toLowerCase() || 'unknown',
            name: l.name || l.full_name || 'Unknown Language',
            code: l.code || l.id?.toUpperCase() || 'UN',
            flag: l.flag || l.avatar || getCountryCodeForLanguage(l.id),
            enabled: l.enabled !== false && l.enabled !== 0 && l.enabled !== '0',
            isDefault: l.is_default === 1 || l.is_default === '1' || l.isDefault === true || l.is_default === true,
          }));
        }
      }

      // Deduplicate by normalized language key
      const seenMap = new Map<string, Language>();
      processedLanguages.forEach((l) => {
        const norm = normalizeLangKey(l.id);
        if (!seenMap.has(norm)) {
          seenMap.set(norm, l);
        }
      });
      const uniqueLanguages = Array.from(seenMap.values());

      // Sort so configured default language is at index 0, followed by Vietnamese, then preserve order
      uniqueLanguages.sort((a, b) => {
        const aDef = Boolean(a.isDefault);
        const bDef = Boolean(b.isDefault);
        if (aDef && !bDef) return -1;
        if (!aDef && bDef) return 1;
        const aVi = a.id === 'vn' || a.id === 'vi';
        const bVi = b.id === 'vn' || b.id === 'vi';
        if (aVi && !bVi) return -1;
        if (!aVi && bVi) return 1;
        return 0;
      });

      const enabledLanguages = uniqueLanguages.length > 0 ? uniqueLanguages : [];

      if (enabledLanguages.length > 0) {
        setLanguages(enabledLanguages);
        loadedProjectIdRef.current = targetProjectId;
        setLanguagesError(null);

        const stored = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
        const matching = stored
          ? enabledLanguages.find(lang =>
            normalizeLangKey(lang.id) === normalizeLangKey(stored) ||
            normalizeLangKey(lang.code) === normalizeLangKey(stored)
          )
          : null;

        const defaultLang = matching || enabledLanguages.find(lang => lang.isDefault) || enabledLanguages[0];
        setCurrentLanguage(defaultLang);
      } else {
        console.warn('⚠️ No languages found in API response');
        throw new Error(`Dự án "${targetProjectId}" không có ngôn ngữ nào khả dụng.`);
      }
    } catch (error: any) {
      if (reqId !== activeRequestIdRef.current) return;
      console.error('❌ Error loading project languages:', error);
      const errorMessage = error?.message || 'Không thể tải danh sách ngôn ngữ dự án.';
      setLanguagesError(errorMessage);

      if (!isSameProject) {
        // Project switch or initial load failed: do NOT keep old languages from project A
        // and do NOT silently fake [vi, en] fallback which hides KM
        setLanguages([]);
        setCurrentLanguage(null);
        loadedProjectIdRef.current = null;
      } else {
        // Same project reload failed: retain previously loaded languages for this project
      }
    } finally {
      if (reqId === activeRequestIdRef.current) {
        setLanguagesLoading(false);
      }
    }
  }, []);

  // Get language by code
  const getLanguageByCode = useCallback((code: string): Language | undefined => {
    return languages.find(lang =>
      lang.id.toLowerCase() === code.toLowerCase() ||
      lang.code.toLowerCase() === code.toLowerCase()
    );
  }, [languages]);

  // Get default language
  const getDefaultLanguage = useCallback((): Language | undefined => {
    return languages.find(lang => lang.isDefault) || languages[0];
  }, [languages]);

  // Get enabled languages
  const getEnabledLanguages = useCallback((): Language[] => {
    return languages.filter(lang => lang.enabled);
  }, [languages]);

  // Format language list as object for easy access
  const formatLanguageList = useCallback((): Record<string, Language> => {
    const formatted: Record<string, Language> = {};
    languages.forEach(lang => {
      formatted[lang.id] = lang;
      formatted[lang.code] = lang;
      formatted[lang.code.toLowerCase()] = lang;
    });
    return formatted;
  }, [languages]);

  // Check if language is supported
  const isLanguageSupported = useCallback((code: string): boolean => {
    return !!getLanguageByCode(code);
  }, [getLanguageByCode]);

  // Get language flag
  const getLanguageFlag = useCallback((code: string): string | undefined => {
    const language = getLanguageByCode(code);
    return language?.flag;
  }, [getLanguageByCode]);

  // Get language name
  const getLanguageName = useCallback((code: string): string | undefined => {
    const language = getLanguageByCode(code);
    return language?.name;
  }, [getLanguageByCode]);

  // Initialize dynamic titles with empty values
  const initializeDynamicTitles = useCallback((): Record<string, string> => {
    const titles: Record<string, string> = {};
    languages.forEach(lang => {
      titles[lang.id] = '';
    });
    return titles;
  }, [languages]);

  // Validate if any title is filled
  const validateDynamicTitles = useCallback((titles: Record<string, string>): boolean => {
    return languages.some(lang => titles[lang.id]?.trim());
  }, [languages]);

  // Get display title (prefer default language, fallback to first available)
  const getDisplayTitle = useCallback((titles: Record<string, string>): string => {
    const defaultLang = getDefaultLanguage();
    if (defaultLang && titles[defaultLang.id]?.trim()) {
      return titles[defaultLang.id];
    }

    // Fallback to first available title
    for (const lang of languages) {
      if (titles[lang.id]?.trim()) {
        return titles[lang.id];
      }
    }

    return '';
  }, [getDefaultLanguage, languages]);

  // Load languages on mount
  useEffect(() => {
    if (!idPathLoading && idPath) {
      loadLanguages();
    }
  }, [idPath, idPathLoading, loadLanguages]);

  // Keep currentLanguage in sync with language change events or localStorage
  useEffect(() => {
    if (typeof window === 'undefined' || languages.length === 0) return;

    const handleSync = (e?: any) => {
      const targetLocale = e?.detail?.locale || localStorage.getItem('language');
      if (!targetLocale) return;
      const matched = languages.find(lang =>
        normalizeLangKey(lang.id) === normalizeLangKey(targetLocale) ||
        normalizeLangKey(lang.code) === normalizeLangKey(targetLocale)
      );
      if (matched && matched.id !== currentLanguage?.id) {
        setCurrentLanguage(matched);
      }
    };

    window.addEventListener('sg3d_language_change', handleSync as EventListener);
    window.addEventListener('storage', handleSync);
    return () => {
      window.removeEventListener('sg3d_language_change', handleSync as EventListener);
      window.removeEventListener('storage', handleSync);
    };
  }, [languages, currentLanguage?.id]);

  // Context value
  const value: LanguageContextType = useMemo(() => ({
    // States
    languages,
    languagesLoading,
    languagesError,
    currentLanguage,

    // Actions
    loadLanguages,
    setCurrentLanguage,
    getLanguageByCode,
    getDefaultLanguage,
    getEnabledLanguages,

    // Utilities
    formatLanguageList,
    isLanguageSupported,
    getLanguageFlag,
    getLanguageName,

    // Dynamic title utilities
    initializeDynamicTitles,
    validateDynamicTitles,
    getDisplayTitle,
  }), [
    languages,
    languagesLoading,
    languagesError,
    currentLanguage,
    loadLanguages,
    getLanguageByCode,
    getDefaultLanguage,
    getEnabledLanguages,
    formatLanguageList,
    isLanguageSupported,
    getLanguageFlag,
    getLanguageName,
    initializeDynamicTitles,
    validateDynamicTitles,
    getDisplayTitle,
  ]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguages = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguages must be used within a LanguageProvider');
  }
  return context;
};

// Export types and defaults
export { DEFAULT_LANGUAGES }; 